import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { PurchaseCount, PurchaseCountsByType, PurchaseType } from '../types';
import { usePurchasingApi } from './use-purchasing-api';

type SalesMetricPeriod = {
  start: Date;
  end: Date;
  data: PurchaseCount[];
};

export type SalesComparisonReport = {
  previous: SalesMetricPeriod;
  current: SalesMetricPeriod;
  countsByType: PurchaseCountsByType;
  hasEverPurchased: boolean;
};

export type SalesPerformanceOptions = {
  start: Date;
  end: Date;
  type?: PurchaseType;
};

const QK_SALES_PERFORMANCE = (start: Date, end: Date, type?: PurchaseType) =>
  ['purchases', 'sales-performance', start, end, type] as const;

function splitIntoPrevAndCurr(purchaseCounts: PurchaseCount[], boundary: Date) {
  const prevPurchases: PurchaseCount[] = [];
  const currPurchases: PurchaseCount[] = [];

  const boundaryTime = boundary.getTime();

  purchaseCounts.forEach((purchase) => {
    const purchaseTime = new Date(purchase.date).getTime();

    if (purchaseTime < boundaryTime) {
      prevPurchases.push(purchase);
    } else {
      currPurchases.push(purchase);
    }
  });

  return [prevPurchases, currPurchases] as const;
}

export function useSalesPerformance({ start, end, type }: SalesPerformanceOptions) {
  const { purchasingApi } = usePurchasingApi();

  return useQuery<SalesComparisonReport>({
    queryKey: QK_SALES_PERFORMANCE(start, end, type),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const prevStart = new Date(start.getTime() - (end.getTime() - start.getTime()));
      const prevEnd = start;

      const hasEverPurchasedPromise = purchasingApi.getPurchases({ limit: 1 });

      const purchaseCountsPromise = purchasingApi.getPurchaseCounts({
        dates: { submitted: { gte: prevStart, lte: end } },
        type,
      });
      const purchaseCountsByTypePromise = purchasingApi.getPurchaseCountsByType({
        dates: { submitted: { gte: prevStart, lte: end } },
      });

      const [hasEverPurchase, purchaseCounts, purchaseCountsByType] = await Promise.all([
        hasEverPurchasedPromise,
        purchaseCountsPromise,
        purchaseCountsByTypePromise,
      ]);

      const [prevPurchases, currPurchases] = splitIntoPrevAndCurr(purchaseCounts, start);

      return {
        previous: {
          start: prevStart,
          end: prevEnd,
          data: prevPurchases,
        },
        current: {
          start,
          end,
          data: currPurchases,
        },
        countsByType: purchaseCountsByType,
        hasEverPurchased: hasEverPurchase.length > 0,
      };
    },
  });
}
